/* ==========================================================================
   Speedy Food — form.js
   State machine for the multi-step "Richiedi consulenza" form.

   Behaviors:
   - Switches between HORECA (6 step) and Distributore (5 step) rami based on
     Step 1 (BigRadioCard selection).
   - Avanti / Indietro navigation with required-field validation on every step
     (i campi obbligatori dei primi step servono al contratto CRM).
   - LocalStorage persist with 24h TTL (key `sf_lead_form_v1`).
   - Auto-save indicator pulses based on lastEdit timestamp.
   - Honeypot anti-spam: if `hp_company` field non-empty, silently reject submit.
   - Consenso privacy obbligatorio: senza spunta l'invio è bloccato.
   - Submit POST to settings.form_endpoint_url (set via data attribute on .fm-shell).
     Payload mappato sul contratto `leadSchema` condiviso con la landing → stesso
     Supabase, stesso CRM. `source: "website"` marca la sorgente "sito".
     If endpoint missing: console.log payload + redirect /pages/grazie?ramo={ramo}.
   - GTM events via window.dataLayer.push if available.
   - Mobile/desktop UI handled by CSS — JS just toggles step visibility.

   Wired in: sections/form-shell.liquid (via <script src="{{ 'form.js' | asset_url }}" defer>)
   ========================================================================== */
(function () {
  'use strict';

  var shell = document.querySelector('[data-fm-shell]');
  if (!shell) return;

  var form = shell.querySelector('[data-fm-form]');
  var honeypot = shell.querySelector('[data-fm-honeypot]');
  var endpoint = shell.getAttribute('data-fm-endpoint') || '';

  var STORAGE_KEY = 'sf_lead_form_v1';
  var TTL_MS = 24 * 60 * 60 * 1000; // 24h

  // Le stringhe che il JS compone a runtime arrivano tradotte da form-shell.liquid,
  // che le stampa in un blocco JSON. I segnaposto sono {n}, {max}, {total}, {area}.
  var I18N = {};
  try {
    var i18nEl = shell.querySelector('[data-fm-i18n]');
    if (i18nEl) {
      I18N = JSON.parse(i18nEl.textContent) || {};
      // Il filtro `t` di Liquid fa l'escape HTML, e dentro un <script> il browser
      // non lo scioglie: senza questo passaggio "You've" arriverebbe "You&#39;ve".
      var dec = document.createElement('textarea');
      Object.keys(I18N).forEach(function (k) {
        if (typeof I18N[k] !== 'string') return;
        dec.innerHTML = I18N[k];
        I18N[k] = dec.value;
      });
    }
  } catch (e) { /* ignore: i fallback qui sotto sono in italiano */ }
  function tr(key, fallback, vars) {
    var s = I18N[key] || fallback;
    if (vars) Object.keys(vars).forEach(function (k) {
      s = s.split('{' + k + '}').join(vars[k]);
    });
    return s;
  }

  // I nomi degli step vivono nel markup della sidebar (gia' tradotto dal locale):
  // leggerli da li' evita di tenerne una seconda copia qui dentro.
  function stepAreas(ramo) {
    var list = shell.querySelector('[data-fm-sidebar-list][data-fm-ramo="' + ramo + '"]');
    if (!list) return [];
    return [].map.call(list.querySelectorAll('[data-fm-sidebar-step]'), function (li) {
      var a = li.querySelector('.fm-sidebar__a');
      return { n: parseInt(li.getAttribute('data-fm-sidebar-step'), 10), area: a ? a.textContent.trim() : '' };
    });
  }
  var STEPS_HORECA = stepAreas('horeca');
  var STEPS_DIST = stepAreas('distributore');
  // Failsafe: se la sidebar non c'e', servono almeno i conteggi giusti (6 e 5 step).
  if (!STEPS_HORECA.length) STEPS_HORECA = [1, 2, 3, 4, 5, 6].map(function (n) { return { n: n, area: '' }; });
  if (!STEPS_DIST.length) STEPS_DIST = [1, 2, 3, 4, 5].map(function (n) { return { n: n, area: '' }; });

  // ─────────────── State ───────────────
  var state = {
    ramo: null,            // 'horeca' | 'distributore' | null (before Step 1 selection)
    step: 1,
    data: {},              // form payload
    lastEdit: Date.now(),
    maxVisited: 1,
  };

  function stepsFor(ramo) { return ramo === 'distributore' ? STEPS_DIST : STEPS_HORECA; }
  function totalSteps() { return stepsFor(state.ramo).length; }

  // ─────────────── LocalStorage ───────────────
  function loadState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var obj = JSON.parse(raw);
      if (!obj || !obj.savedAt) return;
      if (Date.now() - obj.savedAt > TTL_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      Object.assign(state, obj.state || {});
    } catch (e) { /* ignore */ }
  }
  function saveState() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ savedAt: Date.now(), state: state }));
    } catch (e) { /* ignore */ }
  }
  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
  }

  // ─────────────── URL query prefill (?cluster=, ?p=) ───────────────
  function urlPrefill() {
    try {
      var p = new URLSearchParams(window.location.search);
      var cluster = p.get('cluster');
      var product = p.get('p');
      if (cluster) state.data.context_cluster = cluster;
      if (product) state.data.context_product = product;
    } catch (e) { /* ignore */ }
  }

  // ─────────────── Step visibility ───────────────
  function showCurrentStep() {
    var sections = form.querySelectorAll('[data-fm-step]');
    sections.forEach(function (s) {
      var stepN = parseInt(s.getAttribute('data-fm-step'), 10);
      var stepRamo = s.getAttribute('data-fm-ramo');
      var visible = false;
      if (stepN === 1 && stepRamo === 'any') {
        visible = state.step === 1;
      } else if (state.ramo && stepRamo === state.ramo && stepN === state.step) {
        visible = true;
      }
      s.classList.toggle('is-active', visible);
      s.hidden = !visible;
      if (visible) {
        s.classList.add('in-view');
        s.querySelectorAll('.reveal').forEach(function (r) { r.classList.add('in-view'); });
      }
    });
    updateSidebar();
    updateProgressBar();
    updateNav();
    scrollToTopOfForm();
  }

  function scrollToTopOfForm() {
    var main = shell.querySelector('[data-fm-main]');
    if (main && main.scrollIntoView) main.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ─────────────── Sidebar ───────────────
  function updateSidebar() {
    var lists = shell.querySelectorAll('[data-fm-sidebar-list]');
    lists.forEach(function (list) {
      var ramo = list.getAttribute('data-fm-ramo');
      var show = ramo === state.ramo || (state.ramo === null && ramo === 'horeca');
      list.hidden = !show;
      list.querySelectorAll('[data-fm-sidebar-step]').forEach(function (li) {
        var n = parseInt(li.getAttribute('data-fm-sidebar-step'), 10);
        li.classList.toggle('is-current', n === state.step);
        li.classList.toggle('is-visited', n < state.maxVisited);
        li.classList.toggle('is-future', n > state.maxVisited);
      });
    });
  }

  // ─────────────── Progress bar (mobile) ───────────────
  function updateProgressBar() {
    var pbar = shell.querySelector('[data-fm-pbar]');
    if (!pbar) return;
    var total = totalSteps();
    var pct = Math.max(8, Math.round((state.step / total) * 100));
    var fill = pbar.querySelector('[data-fm-pbar-fill]');
    var label = pbar.querySelector('[data-fm-pbar-label]');
    var track = pbar.querySelector('[data-fm-pbar-track]');
    if (fill) fill.style.width = pct + '%';
    if (track) {
      track.setAttribute('aria-valuenow', String(state.step));
      track.setAttribute('aria-valuemax', String(total));
    }
    var steps = stepsFor(state.ramo);
    var area = (steps[state.step - 1] && steps[state.step - 1].area) || '';
    if (label) {
      label.textContent = tr('step_of', 'Passo {n} di {total}', { n: state.step, total: total })
        + (state.step > 1 && area ? ' · ' + area : '');
    }
  }

  // ─────────────── Nav (Avanti / Indietro / Invia) ───────────────
  function updateNav() {
    var back = shell.querySelector('[data-fm-nav="back"]');
    var next = shell.querySelector('[data-fm-nav="next"]');
    var submit = shell.querySelector('[data-fm-nav="submit"]');
    if (!back || !next || !submit) return;
    back.disabled = state.step === 1;
    var isLast = state.ramo && state.step === totalSteps();
    next.hidden = isLast;
    submit.hidden = !isLast;
    // Step 1 → next disabled until ramo selected
    if (state.step === 1) next.disabled = !state.ramo;
    else next.disabled = false;
  }

  // ─────────────── BigRadioCard (Step 1) ───────────────
  function bindBigCards() {
    var cards = shell.querySelectorAll('[data-fm-bigcard]');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        var value = card.getAttribute('data-value');
        state.ramo = value;
        state.data.ramo = value;
        markEdit();
        // Update visual selection within siblings
        cards.forEach(function (c) {
          var isOn = c.getAttribute('data-value') === value;
          c.classList.toggle('is-on', isOn);
          c.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        });
        updateNav();
        updateSidebar();
      });
      // Restore selection on load
      if (card.getAttribute('data-value') === state.ramo) {
        card.classList.add('is-on');
        card.setAttribute('aria-pressed', 'true');
      }
    });
  }

  // ─────────────── ChipGroup (single-select) ───────────────
  function bindChipGroups() {
    var groups = shell.querySelectorAll('[data-fm-chip-group]');
    groups.forEach(function (group) {
      var name = group.getAttribute('data-fm-name');
      var chips = group.querySelectorAll('[data-fm-chip]');
      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var value = chip.getAttribute('data-value');
          state.data[name] = value;
          markEdit();
          chips.forEach(function (c) {
            var isOn = c === chip;
            c.classList.toggle('is-on', isOn);
            c.setAttribute('aria-checked', isOn ? 'true' : 'false');
          });
        });
        // Restore selection
        if (state.data[name] && chip.getAttribute('data-value') === state.data[name]) {
          chip.classList.add('is-on');
          chip.setAttribute('aria-checked', 'true');
        }
      });
    });
  }

  // ─────────────── MultiSelectChips (max + LIFO) ───────────────
  function bindMultiChips() {
    var groups = shell.querySelectorAll('[data-fm-multi]');
    groups.forEach(function (group) {
      var name = group.getAttribute('data-fm-name');
      var max = parseInt(group.getAttribute('data-fm-max'), 10) || 3;
      var counter = group.querySelector('[data-fm-multi-count]');
      var chips = group.querySelectorAll('[data-fm-multi-chip]');
      var selected = state.data[name] || [];
      if (!Array.isArray(selected)) selected = [];
      state.data[name] = selected;

      function render() {
        chips.forEach(function (chip) {
          var v = chip.getAttribute('data-value');
          var isOn = selected.indexOf(v) !== -1;
          var disabled = selected.length >= max && !isOn;
          chip.classList.toggle('is-on', isOn);
          chip.classList.toggle('is-disabled', disabled);
          chip.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        });
        if (counter) counter.textContent = tr('chosen', 'Hai scelto {n} / {max}', { n: selected.length, max: max });
      }

      chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var v = chip.getAttribute('data-value');
          var idx = selected.indexOf(v);
          if (idx !== -1) {
            selected.splice(idx, 1);
          } else if (selected.length < max) {
            selected.push(v);
          } else {
            // LIFO: drop oldest (first selected) and add new
            selected.shift();
            selected.push(v);
          }
          state.data[name] = selected;
          markEdit();
          render();
        });
      });
      render();
    });
  }

  // ─────────────── RadioList ───────────────
  function bindRadioLists() {
    var lists = shell.querySelectorAll('[data-fm-rlist]');
    lists.forEach(function (list) {
      var name = list.getAttribute('data-fm-name');
      var rows = list.querySelectorAll('[data-fm-rlist-row]');
      rows.forEach(function (row) {
        var input = row.querySelector('[data-fm-rlist-input]');
        var extra = row.querySelector('[data-fm-rlist-extra]');
        row.addEventListener('click', function (e) {
          var value = row.getAttribute('data-value');
          if (input) input.checked = true;
          state.data[name] = value;
          markEdit();
          rows.forEach(function (r) {
            var isOn = r === row;
            r.classList.toggle('is-on', isOn);
            var rExtra = r.querySelector('[data-fm-rlist-extra]');
            if (rExtra) rExtra.hidden = !isOn;
          });
        });
        // Restore
        if (state.data[name] === row.getAttribute('data-value')) {
          if (input) input.checked = true;
          row.classList.add('is-on');
          if (extra) extra.hidden = false;
        }
      });
    });
  }

  // ─────────────── Inputs (text/email/tel/number) ───────────────
  function bindInputs() {
    var wrappers = shell.querySelectorAll('[data-fm-input]');
    wrappers.forEach(function (w) {
      var name = w.getAttribute('data-fm-name');
      var input = w.querySelector('input, textarea');
      if (!input) return;
      // Restore
      if (state.data[name]) input.value = state.data[name];
      // Focus/blur for visual states
      input.addEventListener('focus', function () { w.classList.add('is-focus'); });
      input.addEventListener('blur', function () {
        w.classList.remove('is-focus');
        w.classList.toggle('has-value', !!input.value);
      });
      input.addEventListener('input', function () {
        state.data[name] = input.value;
        w.classList.toggle('has-value', !!input.value);
        // Hide error on edit
        var err = w.querySelector('.fm-input__err');
        if (err) err.hidden = true;
        w.classList.remove('is-error');
        markEdit();

        // Textarea counter
        if (input.matches('[data-fm-textarea]')) {
          var max = parseInt(input.getAttribute('data-fm-max'), 10) || 500;
          var count = w.querySelector('[data-fm-count]');
          if (count) count.textContent = input.value.length + ' / ' + max;
        }
      });
      if (input.value) w.classList.add('has-value');
      // Il contatore va allineato anche al restore, non solo mentre si digita.
      if (input.matches('[data-fm-textarea]')) {
        var maxR = parseInt(input.getAttribute('data-fm-max'), 10) || 500;
        var countR = w.querySelector('[data-fm-count]');
        if (countR) countR.textContent = input.value.length + ' / ' + maxR;
      }
    });
  }

  // ─────────────── Consent checkbox ───────────────
  // Obbligatoria: senza consenso il lead non è ricontattabile e l'intake CRM lo rifiuta.
  function bindConsent() {
    var boxes = shell.querySelectorAll('[data-fm-consent-box]');
    boxes.forEach(function (box) {
      var wrap = box.closest('[data-fm-consent]');
      if (state.data.consenso) box.checked = true;
      box.addEventListener('change', function () {
        state.data.consenso = box.checked;
        if (box.checked && wrap) {
          wrap.classList.remove('is-error');
          var err = wrap.querySelector('.fm-input__err');
          if (err) err.hidden = true;
        }
        markEdit();
      });
    });
  }

  // ─────────────── Validation ───────────────
  // Gira su OGNI step, non solo sull'ultimo: i campi obbligatori dei primi step
  // (es. comune) sono richiesti dal contratto CRM e devono essere raccolti prima.
  function validateStep(stepN) {
    var ramo = state.ramo;
    if (stepN === 1) return !!ramo;

    var requiredWrappers = shell.querySelectorAll('[data-fm-step="' + stepN + '"][data-fm-ramo="' + ramo + '"] [data-fm-required="true"]');
    var ok = true;
    var firstBad = null;

    requiredWrappers.forEach(function (w) {
      var bad;
      if (w.hasAttribute('data-fm-consent')) {
        var box = w.querySelector('[data-fm-consent-box]');
        bad = !(box && box.checked);
      } else {
        var input = w.querySelector('input, textarea');
        bad = !(input && input.value.trim());
      }
      w.classList.toggle('is-error', bad);
      var err = w.querySelector('.fm-input__err');
      if (err) err.hidden = !bad;
      if (bad && !firstBad) firstBad = w;
      if (bad) ok = false;
    });

    if (firstBad) {
      var focusable = firstBad.querySelector('input, textarea');
      if (focusable) {
        try { focusable.focus({ preventScroll: true }); } catch (e) { focusable.focus(); }
        firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    return ok;
  }

  // ─────────────── CRM payload ───────────────
  // Contratto `leadSchema` (packages/lib/src/schema.ts), condiviso con la landing:
  // stesso endpoint, stesso Supabase, stesso CRM. `source: "website"` è ciò che il
  // backend traduce in sorgente "sito" per distinguerlo dai lead della landing.
  var TIPO_LOCALE_FALLBACK = 'Altro';

  function cookie(name) {
    try {
      var m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? m.pop() : null;
    } catch (e) { return null; }
  }

  // Etichetta leggibile di un valore radio: nel CRM l'operatore deve leggere
  // "Oltre 500.000 pezzi / anno", non "fascia_d".
  function labelFor(name, value) {
    if (!value) return '';
    try {
      var el = shell.querySelector('[data-fm-name="' + name + '"] [data-value="' + value + '"]');
      var lab = el && el.querySelector('.fm-rlist__lab');
      if (lab) return lab.textContent.trim();
    } catch (e) { /* ignore */ }
    return value;
  }

  function line(label, value) {
    if (value === null || value === undefined) return '';
    var v = Array.isArray(value) ? value.join(', ') : String(value);
    v = v.trim();
    return v ? label + ': ' + v + '\n' : '';
  }

  // Le risposte che non hanno una colonna dedicata confluiscono in `migliorare`,
  // che il CRM mostra per intero nel drawer del lead.
  function buildPayload() {
    var d = state.data;
    var isDist = state.ramo === 'distributore';
    var det = '';

    if (isDist) {
      det += 'RICHIESTA DISTRIBUTORE / PARTNERSHIP\n';
      det += line('Ragione sociale', d.d_ragione_sociale);
      det += line('Ruolo', d.d_ruolo);
      det += line('Canale', labelFor('d_canale', d.d_canale));
      if (d.d_canale === 'altro') det += line('Canale (dettaglio)', d.d_canale_altro);
      det += line('Geografia', d.d_geo);
      det += line('Canali serviti', d.d_canali_serviti);
      det += line('Categorie', d.d_categorie);
      det += line('Volumi', labelFor('d_volumi', d.d_volumi));
      det += line('Note', d.d_note);
    } else {
      det += line('Attività', d.h_nome_attivita);
      det += line('Provincia', d.h_provincia);
      det += line('Interesse', d.h_interesse);
      det += line('Da risolvere', d.h_nodi);
      det += line('Tempi', d.h_tempi);
      det += line('Ruolo', d.h_ruolo);
      det += line('Contatto preferito', d.h_canale_pref);
      det += line('Note', d.h_note);
    }
    det += line('Cluster di provenienza', d.context_cluster);
    det += line('Prodotto di provenienza', d.context_product);

    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { q = null; }
    function utm(k) { return (q && q.get(k)) || null; }

    return {
      nome: (isDist ? d.d_contatto : d.h_referente) || '',
      tipo_locale: isDist ? TIPO_LOCALE_FALLBACK : (d.h_tipologia || TIPO_LOCALE_FALLBACK),
      comune: (isDist ? d.d_comune : d.h_comune) || '',
      whatsapp: (isDist ? d.d_telefono : d.h_telefono) || '',
      email: (isDist ? d.d_email : d.h_email) || null,
      migliorare: det.trim().slice(0, 2000),
      consenso: d.consenso === true,
      hp: honeypot ? honeypot.value : '',
      page_url: window.location.href,
      source: 'website',
      // Discrimina il ramo finché il CRM non ha una colonna dedicata.
      lp_variant: 'sito-' + (state.ramo || 'horeca'),
      utm_source: utm('utm_source'),
      utm_medium: utm('utm_medium'),
      utm_campaign: utm('utm_campaign'),
      utm_content: utm('utm_content'),
      utm_term: utm('utm_term'),
      fbp: cookie('_fbp'),
      fbc: cookie('_fbc'),
    };
  }

  // ─────────────── Submit ───────────────
  function submitForm() {
    // Honeypot check
    if (honeypot && honeypot.value) {
      console.warn('[form] honeypot triggered — submission ignored');
      return;
    }
    if (!validateStep(state.step)) return;

    var payload = buildPayload();

    // GTM event
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'form_lead_submit_' + state.ramo,
        ramo: state.ramo,
        cluster: state.data.context_cluster || null,
        product: state.data.context_product || null,
      });
    } catch (e) { /* ignore */ }

    function goToThanks() {
      window.location.href = '/pages/grazie?ramo=' + encodeURIComponent(state.ramo);
    }
    function done() {
      clearState();
      goToThanks();
    }

    if (!endpoint) {
      // No endpoint configured — log + redirect to grazie
      console.log('[form] no endpoint configured, payload:', payload);
      done();
      return;
    }

    // Due tentativi: un errore di rete non deve costare un lead.
    // In caso di fallimento NON svuoto lo storage, così le risposte restano
    // recuperabili per 24h e l'utente non deve ricompilare da zero.
    function post(attempt) {
      return fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).then(function (r) {
        if (r.ok) { done(); return; }
        if (r.status >= 400 && r.status < 500) {
          // Payload rifiutato: ritentare non cambia nulla, serve vederlo nei log.
          return r.text().then(function (body) {
            console.error('[form] lead rifiutato (HTTP ' + r.status + '):', body);
            goToThanks();
          });
        }
        throw new Error('HTTP ' + r.status);
      }).catch(function (err) {
        if (attempt < 2) {
          return new Promise(function (res) { setTimeout(res, 800); })
            .then(function () { return post(attempt + 1); });
        }
        console.error('[form] invio fallito dopo 2 tentativi:', err);
        goToThanks();
      });
    }
    post(1);
  }

  // ─────────────── Mark edit (for auto-save indicator) ───────────────
  function markEdit() {
    state.lastEdit = Date.now();
    saveState();
  }

  // ─────────────── Auto-save indicator ───────────────
  function tickAutoSave() {
    var el = shell.querySelector('[data-fm-save-label]');
    if (!el) return;
    var elapsed = Math.max(0, Math.floor((Date.now() - state.lastEdit) / 1000));
    var label;
    if (elapsed < 5) label = tr('saved_now', 'Salvato adesso');
    else if (elapsed < 60) label = tr('saved_secs', 'Salvato {n}s fa', { n: Math.floor(elapsed / 5) * 5 });
    else label = tr('saved_minute', 'Salvato un minuto fa');
    el.textContent = label;
  }

  // ─────────────── Navigation ───────────────
  function bindNav() {
    var back = shell.querySelector('[data-fm-nav="back"]');
    var next = shell.querySelector('[data-fm-nav="next"]');
    var submit = shell.querySelector('[data-fm-nav="submit"]');

    if (back) back.addEventListener('click', function () {
      if (state.step > 1) {
        state.step--;
        saveState();
        showCurrentStep();
      }
    });
    if (next) next.addEventListener('click', function () {
      if (state.step === 1 && !state.ramo) return;
      if (!validateStep(state.step)) return;
      var total = totalSteps();
      if (state.step < total) {
        state.step++;
        state.maxVisited = Math.max(state.maxVisited, state.step);
        saveState();
        // GTM step advance event
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'form_lead_step_advance',
            ramo: state.ramo,
            step: state.step,
          });
        } catch (e) { /* ignore */ }
        showCurrentStep();
      }
    });
    if (form) form.addEventListener('submit', function (e) {
      e.preventDefault();
      submitForm();
    });
  }

  // ─────────────── Init ───────────────
  loadState();
  urlPrefill();
  bindBigCards();
  bindChipGroups();
  bindMultiChips();
  bindRadioLists();
  bindInputs();
  bindConsent();
  bindNav();
  showCurrentStep();
  setInterval(tickAutoSave, 1000);
})();
